import { FC } from 'react';
import {
  ActionsMenu,
  Button,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  TruncatedText,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP } from 'config';
import { useSearchParams } from 'utils/navigation';
import { useModalContext } from 'store/modal';
import { PromptModal } from 'containers/admin';
import { Filters, OpenSearchFilter } from 'components/filters';
import {
  promptsFiltersSchema,
  useDeletePrompt,
  usePrompts,
} from 'store/prompts';
import moment from 'moment';
import { usePredefinedToasts } from 'utils/Toast';

export const AdminBrandPromptsList: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();

  const { searchParams: filters, navigate } =
    useSearchParams(promptsFiltersSchema);

  const { data: prompts, isLoading } = usePrompts(filters);

  const { mutate: deletePrompt, isLoading: isDeleting } = useDeletePrompt();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <Button
              label={t('common.add')}
              onClick={() => {
                openModal({
                  component: PromptModal,
                });
              }}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={prompts?.total} />

            <Stack direction="row" alignItems="center" gap="10px">
              <Filters
                value={filters}
                initialValues={promptsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, setFilter }) => (
                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={prompts?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: prompt } }) => prompt.name,
            meta: { sort: true },
          },
          {
            id: 'created_at',
            header: t('marketplaceOrders.orderDate'),
            cell: ({ row: { original: prompt } }) =>
              moment(prompt.createdAt).format('l LT'),
            meta: { sort: true },
          },
          {
            id: 'content',
            header: t('benefitTemplates.templateContent'),
            cell: ({ row: { original: prompt } }) => (
              <TruncatedText
                text={prompt.prompt}
                maxLength={300}
                textProps={{ color: 'inherit' }}
              />
            ),
          },
          {
            id: 'actions',
            cell: ({ row: { original: prompt } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('common.edit'),
                      onClick: () => {
                        openModal({
                          component: PromptModal,
                          props: {
                            promptId: prompt.id,
                            initialValues: {
                              name: prompt.name,
                              prompt: prompt.prompt,
                            },
                          },
                        });
                      },
                    },
                    {
                      label: t('actions.delete'),
                      onClick: () => {
                        deletePrompt(
                          { promptId: prompt.id },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.deleted();
                            },
                          },
                        );
                      },
                      color: 'error',
                      disabled: isDeleting,
                    },
                  ]}
                />
              );
            },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          prompts && {
            count: prompts.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
